import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getUserPreferences,
  updateUserPreferences,
  UserPreferences,
} from "@/hooks/useFirestore";
import {
  NativeLanguage,
  TranslationDirection,
  UserGender,
  HebrewLevel,
  LearningGoal,
} from "@/types";

interface UserPreferencesContextType {
  preferences: UserPreferences;
  loading: boolean;
  setNativeLanguage: (lang: NativeLanguage) => Promise<void>;
  setDefaultTranslationDirection: (dir: TranslationDirection) => Promise<void>;
  setAutoCategories: (enabled: boolean) => Promise<void>;
  setGender: (gender: UserGender | undefined) => Promise<void>;
  setOnboardingCompleted: (completed: boolean) => Promise<void>;
  setWalkthroughCompleted: (completed: boolean) => Promise<void>;
  setHebrewLevel: (level: HebrewLevel) => Promise<void>;
  setLearningGoal: (goal: LearningGoal) => Promise<void>;
  setOnboardingPrompt: (prompt: string) => Promise<void>;
  setDailyActivities: (activities: string[]) => Promise<void>;
  setInterests: (interests: string[]) => Promise<void>;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  nativeLanguage: "en",
  defaultTranslationDirection: "he-en",
  autoCategories: true,
  gender: undefined,
  onboardingCompleted: false,
};

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setPreferences(DEFAULT_PREFERENCES);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getUserPreferences(user.uid, { displayName: user.displayName, email: user.email })
      .then((prefs) => {
        if (!cancelled) {
          setPreferences(prefs);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Failed to load user preferences:", err);
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [user]);

  const updatePref = useCallback(
    async (update: Partial<UserPreferences>) => {
      if (!user) return;
      setPreferences((prev) => ({ ...prev, ...update }));
      await updateUserPreferences(user.uid, update);
    },
    [user]
  );

  const setNativeLanguage = useCallback(
    (lang: NativeLanguage) => updatePref({ nativeLanguage: lang }),
    [updatePref]
  );

  const setDefaultTranslationDirection = useCallback(
    (dir: TranslationDirection) => updatePref({ defaultTranslationDirection: dir }),
    [updatePref]
  );

  const setAutoCategories = useCallback(
    (enabled: boolean) => updatePref({ autoCategories: enabled }),
    [updatePref]
  );

  const setGender = useCallback(
    (gender: UserGender | undefined) => updatePref({ gender }),
    [updatePref]
  );

  const setOnboardingCompleted = useCallback(
    (completed: boolean) => updatePref({ onboardingCompleted: completed }),
    [updatePref]
  );

  const setWalkthroughCompleted = useCallback(
    (completed: boolean) => updatePref({ walkthroughCompleted: completed }),
    [updatePref]
  );

  const setHebrewLevel = useCallback(
    (level: HebrewLevel) => updatePref({ hebrewLevel: level }),
    [updatePref]
  );

  const setLearningGoal = useCallback(
    (goal: LearningGoal) => updatePref({ learningGoal: goal }),
    [updatePref]
  );

  const setOnboardingPrompt = useCallback(
    (prompt: string) => updatePref({ onboardingPrompt: prompt }),
    [updatePref]
  );

  const setDailyActivities = useCallback(
    (activities: string[]) => updatePref({ dailyActivities: activities }),
    [updatePref]
  );

  const setInterests = useCallback(
    (interests: string[]) => updatePref({ interests }),
    [updatePref]
  );

  return (
    <UserPreferencesContext.Provider
      value={{
        preferences,
        loading,
        setNativeLanguage,
        setDefaultTranslationDirection,
        setAutoCategories,
        setGender,
        setOnboardingCompleted,
        setWalkthroughCompleted,
        setHebrewLevel,
        setLearningGoal,
        setOnboardingPrompt,
        setDailyActivities,
        setInterests,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error("useUserPreferences must be used within a UserPreferencesProvider");
  }
  return context;
}
